import { RiCheckLine, RiLoader3Line, RiResetRightLine } from '@remixicon/react'
import { format, isSameDay, parseISO } from 'date-fns'
import { useEffect, useState } from 'react'

import type { Detail } from '@/api/orders/orders-types'
import { useMultiUpdateProductionLog, useUpdateProductionLog } from '@/api/production-logs/production-logs'
import { isCompletedDetailStatus } from '@/components/common/selectors/detail-status-selector/config/statuses'
import { Button } from '@/components/ui/button'
import { DatePicker } from '@/components/ui/date-picker'
import { DATE_FORMATS } from '@/constants/app'
import { cn } from '@/lib/utils'

export const AssignDetailDate = ({ detail, className }: { detail: Detail; className?: string }) => {
  const currentProdLog = detail?.production_logs?.at(-1)

  const completed = detail?.item?.status === 'ready_for_pack' || isCompletedDetailStatus(detail?.item?.status)

  const [date, setDate] = useState(
    currentProdLog?.production_date ? new Date(parseISO(currentProdLog?.production_date)) : undefined
  )

  const updateProductionLogMutation = useUpdateProductionLog()
  const multiupdateProductLogMutation = useMultiUpdateProductionLog()

  const onDateChange = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()

    if (date) {
      if (currentProdLog) {
        updateProductionLogMutation.mutate({
          id: currentProdLog.id,
          payload: {
            production_date: format(date, DATE_FORMATS.iso),
            start_time: currentProdLog.start_time ? format(date, DATE_FORMATS.iso) : undefined
          }
        })
      } else if (detail.item?.flow.inven) {
        multiupdateProductLogMutation.mutate({
          payload: {
            origin_items: [detail.autoid],
            flow_inven: detail.item.flow.inven,
            production_date: format(date, DATE_FORMATS.iso)
          }
        })
      }
    }
  }

  const handleResetDate = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    setDate(undefined)
    if (currentProdLog) {
      updateProductionLogMutation.mutate({
        id: currentProdLog.id,
        payload: {
          production_date: null
        }
      })
    }
  }

  useEffect(() => {
    setDate(currentProdLog?.production_date ? new Date(parseISO(currentProdLog?.production_date)) : undefined)
  }, [currentProdLog?.production_date])

  return (
    <div className={cn('mt-2 flex items-center gap-2', className)}>
      <DatePicker
        calendarProps={{
          fromDate: new Date()
        }}
        onClick={(e) => e.stopPropagation()}
        className='h-8 flex-1'
        disabled={!!detail.item?.is_blocked || completed}
        date={date}
        placeholder='Assign Production date'
        setDate={setDate}
      />
      {completed ? null : (
        <>
          <Button
            onClick={handleResetDate}
            size='icon'
            className='hover:[&_svg]:text-white-100 size-8 hover:bg-red-300'
            variant='ghost'
            disabled={
              !date ||
              updateProductionLogMutation.isPending ||
              !Object.keys(detail?.standard_times ?? {}).length ||
              !!detail.item?.is_blocked ||
              completed
            }
          >
            {updateProductionLogMutation.isPending ? (
              <RiLoader3Line className='animate-spin' />
            ) : (
              <RiResetRightLine />
            )}
          </Button>
          <Button
            className='size-8'
            onClick={onDateChange}
            size='icon'
            disabled={
              !date ||
              updateProductionLogMutation.isPending ||
              isSameDay(date, new Date(currentProdLog?.production_date!))
            }
          >
            {updateProductionLogMutation.isPending ? (
              <RiLoader3Line className='animate-spin' />
            ) : (
              <RiCheckLine />
            )}
          </Button>
        </>
      )}
    </div>
  )
}
